/**
 * Ícone Lucide por NOME.
 *
 * O protótipo escreve `<i data-lucide="wallet">`, e a navegação (`navegacao.ts`) é
 * DADO — o ícone é uma string. Para resolver a string em componente sem perder o
 * tree-shaking, o mapa abaixo é explícito.
 *
 * A primeira versão importava o registro inteiro da biblioteca e procurava pelo
 * nome. Funcionava, e trazia **a biblioteca inteira** no bundle: 811 kB, contra as
 * poucas dezenas de ícones que o app realmente usa. Nome dinâmico não se
 * tree-shake — não há como o empacotador saber quais entradas serão pedidas.
 *
 * Um teste exige que TODO nome citado na navegação exista neste mapa, para que
 * acrescentar um item de menu com ícone novo falhe alto em vez de renderizar um
 * buraco.
 */
import {
  ArrowDown,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  BadgeDollarSign,
  Ban,
  Bell,
  BookOpen,
  Briefcase,
  Building,
  Building2,
  Cake,
  Calculator,
  Calendar,
  CalendarClock,
  CalendarPlus,
  Camera,
  ChartColumn,
  ChartPie,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleAlert,
  CircleDot,
  CircleHelp,
  Clock,
  Coins,
  Copy,
  Download,
  ExternalLink,
  EllipsisVertical,
  Eye,
  EyeOff,
  Factory,
  FileMinus,
  FilePlus2,
  FileText,
  FileX,
  Filter,
  Files,
  FolderTree,
  FlaskConical,
  Flag,
  Gauge,
  GitBranch,
  GitCompare,
  GitMerge,
  Globe,
  Goal,
  GripVertical,
  Hammer,
  HandCoins,
  Handshake,
  History,
  Inbox,
  Info,
  KeyRound,
  Landmark,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  Link,
  Link2,
  List,
  ListTree,
  Loader2,
  Lock,
  LockOpen,
  LogIn,
  LogOut,
  Mail,
  MailCheck,
  MessageCircle,
  MessagesSquare,
  Megaphone,
  Menu,
  Minus,
  MoreVertical,
  Package,
  PanelLeft,
  Pencil,
  Percent,
  Phone,
  Plug,
  Plus,
  PlusCircle,
  RadioTower,
  Receipt,
  RefreshCw,
  Repeat,
  RotateCcw,
  Search,
  SearchX,
  Send,
  Settings,
  ShoppingBasket,
  SlidersHorizontal,
  Split,
  Shield,
  ShieldCheck,
  SquareCheckBig,
  SquareKanban,
  Stamp,
  Star,
  Table2,
  Tag,
  Target,
  Timer,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Truck,
  Undo2,
  Upload,
  UserCheck,
  UserMinus,
  UserPlus,
  UserRound,
  UserX,
  Users,
  WandSparkles,
  Wallet,
  Waves,
  X,
  XCircle,
} from 'lucide-react'
import type { ComponentType } from 'react'

type ComponenteDeIcone = ComponentType<{ className: string }>

export const ICONES: Readonly<Record<string, ComponenteDeIcone>> = {
  'arrow-down': ArrowDown,
  'arrow-left': ArrowLeft,
  'arrow-left-right': ArrowLeftRight,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-up-down': ArrowUpDown,
  'arrow-up-right': ArrowUpRight,
  'badge-dollar-sign': BadgeDollarSign,
  'ban': Ban,
  'bell': Bell,
  'book-open': BookOpen,
  'briefcase': Briefcase,
  'building': Building,
  'building-2': Building2,
  'cake': Cake,
  'calculator': Calculator,
  'calendar': Calendar,
  'calendar-clock': CalendarClock,
  'calendar-plus': CalendarPlus,
  'camera': Camera,
  'chart-column': ChartColumn,
  'chart-pie': ChartPie,
  'check': Check,
  'check-circle': CheckCircle,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevrons-up-down': ChevronsUpDown,
  'circle-alert': CircleAlert,
  'circle-dot': CircleDot,
  'circle-help': CircleHelp,
  'clock': Clock,
  'coins': Coins,
  'copy': Copy,
  'download': Download,
  'external-link': ExternalLink,
  'ellipsis-vertical': EllipsisVertical,
  'eye': Eye,
  'eye-off': EyeOff,
  'factory': Factory,
  'file-minus': FileMinus,
  'file-plus-2': FilePlus2,
  'file-text': FileText,
  'file-x': FileX,
  'filter': Filter,
  'files': Files,
  'folder-tree': FolderTree,
  'flask-conical': FlaskConical,
  /* "Alterar status" da ficha da campanha (5.2, D-189): o ícone do botão desenhado. */
  'flag': Flag,
  'gauge': Gauge,
  'git-branch': GitBranch,
  'git-compare': GitCompare,
  'git-merge': GitMerge,
  'globe': Globe,
  'goal': Goal,
  'grip-vertical': GripVertical,
  'hammer': Hammer,
  'hand-coins': HandCoins,
  'handshake': Handshake,
  'history': History,
  'inbox': Inbox,
  'info': Info,
  'key-round': KeyRound,
  'landmark': Landmark,
  'layers': Layers,
  'layout-dashboard': LayoutDashboard,
  'life-buoy': LifeBuoy,
  'link': Link,
  'link-2': Link2,
  'list': List,
  'list-tree': ListTree,
  'loader-2': Loader2,
  'lock': Lock,
  'log-in': LogIn,
  'log-out': LogOut,
  'mail': Mail,
  'mail-check': MailCheck,
  'message-circle': MessageCircle,
  'messages-square': MessagesSquare,
  'megaphone': Megaphone,
  'menu': Menu,
  'minus': Minus,
  'more-vertical': MoreVertical,
  'package': Package,
  'panel-left': PanelLeft,
  'pencil': Pencil,
  // A integração bancária da 6.2 — o protótipo usa `plug-zap`; aqui basta a tomada,
  // porque o raio significaria "conectada" e o bloco nasce desligado.
  'percent': Percent,
  'phone': Phone,
  'plug': Plug,
  'plus': Plus,
  'plus-circle': PlusCircle,
  'radio-tower': RadioTower,
  'receipt': Receipt,
  'refresh-cw': RefreshCw,
  'repeat': Repeat,
  'rotate-ccw': RotateCcw,
  'search': Search,
  'search-x': SearchX,
  'send': Send,
  'settings': Settings,
  'shopping-basket': ShoppingBasket,
  'sliders-horizontal': SlidersHorizontal,
  'split': Split,
  'shield': Shield,
  'shield-check': ShieldCheck,
  'square-check-big': SquareCheckBig,
  'square-kanban': SquareKanban,
  'stamp': Stamp,
  'star': Star,
  'table-2': Table2,
  'tag': Tag,
  'target': Target,
  'timer': Timer,
  'trash-2': Trash2,
  'trending-up': TrendingUp,
  'triangle-alert': TriangleAlert,
  'truck': Truck,
  'undo-2': Undo2,
  'unlock': LockOpen,
  'upload': Upload,
  'user-check': UserCheck,
  'user-minus': UserMinus,
  'user-plus': UserPlus,
  'user-round': UserRound,
  'user-x': UserX,
  'users': Users,
  'wand-sparkles': WandSparkles,
  'wallet': Wallet,
  'waves': Waves,
  'x': X,
  'x-circle': XCircle,
}

export function Icone({ nome, className = '' }: { nome: string; className?: string }) {
  const Componente = ICONES[nome]
  // Ícone ausente não quebra a tela, mas reserva o espaço para o layout não pular.
  if (Componente === undefined) return <span className={className} aria-hidden="true" />
  return <Componente className={className} />
}
